import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth';
import { authApi } from '@/api/auth';
import type { LoginRequest } from '@/types';

export function useLogin() {
  const { login } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: LoginRequest) => authApi.login(credentials),
    onSuccess: (data) => {
      // Wipe any cached query data from a previous session so one org never
      // sees another org's cached sites/portfolios on a shared browser.
      queryClient.clear();
      login(data.user, data.access_token, data.refresh_token);
    },
  });
}

export function useLogout() {
  const { logout } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      logout();
      // Clear all cached query data on logout — otherwise the next user to
      // log in on this browser briefly sees the prior org's cached data.
      queryClient.clear();
    },
    onError: () => {
      logout();
      queryClient.clear();
    },
  });
}

export function useCurrentUser() {
  const { isAuthenticated, setUser } = useAuthStore();

  return useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const user = await authApi.getCurrentUser();
      setUser(user);
      return user;
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: ({
      currentPassword,
      newPassword,
    }: {
      currentPassword: string;
      newPassword: string;
    }) => authApi.changePassword(currentPassword, newPassword),
  });
}

// Product access hooks
export function useProductAccess() {
  const { isAuthenticated, setProducts, _hasHydrated } = useAuthStore();

  return useQuery({
    queryKey: ['productAccess'],
    queryFn: async () => {
      const data = await authApi.getProductAccess();
      setProducts(data.products, data.all_access);
      return data;
    },
    // Only enable after hydration completes AND user is authenticated
    enabled: _hasHydrated && isAuthenticated,
    staleTime: 30 * 1000, // 30 seconds
    retry: 1, // Retry once on failure
  });
}

export function useUnlockProduct() {
  const queryClient = useQueryClient();
  const { addProduct } = useAuthStore();

  return useMutation({
    mutationFn: (accessCode: string) => authApi.unlockProduct(accessCode),
    onSuccess: (data) => {
      // Add the product to local store
      addProduct(data.product);
      // Invalidate product access query to refresh from server
      queryClient.invalidateQueries({ queryKey: ['productAccess'] });
    },
  });
}
